import React, { useState, useEffect, useRef } from "react";
import { Form, Button, Modal } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import "styles/Notes.css";
import NotesService from "services/NotesService";
import {
  NOTE_FETCH_ERROR,
  NOTE_DELETE_ERROR,
  NOTE_ADD_ERROR,
  PLEASE_ENTER_A_NOTE,
  NO_PENDING_NOTES_FOUND,
  NO_RESOLVED_NOTES_FOUND,
  NOTE_ADDED_SUCCESSFULLY,
  LABEL_ADD_A_NOTE,
  NOTE_RESOLVE_ERROR,
  NOTE_RESOLVED_SUCCESSFULLY,
  NOTE_TAGS_FETCH_ERROR,
  NOTE_DELETED_SUCCESSFULLY,
  ADD_NOTE,
} from "configurations/MessageConstants";
import { Spinner } from "react-bootstrap";
import { NotesSvg } from "assets/icons/NotesSvg";
import { NOTES_STATUS } from "configurations/Constants";
import { toast } from "react-toastify";
import NoteTagPicker from "./NoteTagPicker";
import NoteCard from "./NoteCard";
import CardSkeleton from "components/common/CardSkeleton";

const Notes = ({ id, type, name, show, handleClose }) => {
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [notesTags, setNotesTags] = useState([]);
  const [resolvingNoteId, setResolvingNoteId] = useState(null);
  const notesContainerRef = useRef(null);

  const defaultValues = {
    note: "",
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues });

  useEffect(() => {
    getNotes();
  }, [showResolved, type, id]);

  useEffect(() => {
    getNotesTags();
  }, []);

  const getNotes = async () => {
    setIsLoading(true);
    try {
      const params = `status=${
        showResolved ? NOTES_STATUS.RESOLVED : NOTES_STATUS.PENDING
      }`;
      const notes = await NotesService.getNotes(type, id, params);
      setNotes(notes);
    } catch (error) {
      console.error(NOTE_FETCH_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const getNotesTags = async () => {
    try {
      const tags = await NotesService.getNotesTags();
      setNotesTags(tags);
    } catch (error) {
      console.error(NOTE_TAGS_FETCH_ERROR, error);
    }
  };

  const addNewTagsToOptions = (tags) => {
    const existingTagNames = new Set(notesTags.map((tag) => tag.name));
    const newTags = tags.filter((tag) => !existingTagNames.has(tag));
    if (newTags.length > 0) {
      setNotesTags((prevTags) => {
        const allTags = [...prevTags, ...newTags.map((tag) => ({ name: tag }))];
        return allTags.sort((a, b) => a.name.localeCompare(b.name));
      });
    }
  };

  const onSubmit = async (data) => {
    try {
      setIsAdding(true);
      const payload = {
        content: data.note,
        tags: selectedTags,
        status: NOTES_STATUS.PENDING,
      };
      const newNote = await NotesService.addNote(type, id, payload);
      setNotes([newNote, ...notes]);
      addNewTagsToOptions(selectedTags);
      reset(defaultValues);
      setShowResolved(false);
      setSelectedTags([]);
      if (notesContainerRef.current) {
        notesContainerRef.current.scrollTop = 0;
      }
      toast.success(NOTE_ADDED_SUCCESSFULLY);
    } catch (error) {
      console.error(NOTE_ADD_ERROR, error);
      toast.error(NOTE_ADD_ERROR);
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      setIsLoading(true);
      await NotesService.deleteNote(type, id, noteId);
      setNotes(notes.filter((note) => note.id !== noteId));
      toast.success(NOTE_DELETED_SUCCESSFULLY);
    } catch (error) {
      console.error(NOTE_DELETE_ERROR, error);
      toast.error(error.response.data.detail);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolveNote = async (noteId) => {
    const payload = {
      status: NOTES_STATUS.RESOLVED,
    };
    try {
      setResolvingNoteId(noteId);
      const updatedNote = await NotesService.updateNote(
        type,
        id,
        noteId,
        payload
      );
      setNotes(
        notes
          .map((note) => (note.id === noteId ? updatedNote : note))
          .filter((note) => note.status === NOTES_STATUS.PENDING)
      );
      toast.success(NOTE_RESOLVED_SUCCESSFULLY);
    } catch (error) {
      console.error(NOTE_RESOLVE_ERROR, error);
      toast.error(NOTE_RESOLVE_ERROR);
    } finally {
      setResolvingNoteId(null);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="lg"
        className="notes-modal"
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <div className="d-flex align-items-center gap-2">
              <span className="notes-title-content">Notes - {name}</span>
              <div className="notes-type-badge">
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="add-note-container mb-3">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Group className="mb-2">
                <Controller
                  name="note"
                  control={control}
                  rules={{ required: PLEASE_ENTER_A_NOTE }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      as="textarea"
                      rows={2}
                      placeholder={LABEL_ADD_A_NOTE}
                      isInvalid={!!errors.note}
                      maxLength={1000}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.note?.message}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="d-flex justify-content-end align-items-end gap-3">
                <div className="flex-grow-1">
                  <Form.Group>
                    <NoteTagPicker
                      selectedTags={selectedTags}
                      onTagsChange={setSelectedTags}
                      isDisabled={isSubmitting || isAdding}
                      notesTags={notesTags}
                    />
                  </Form.Group>
                </div>
                <div className="flex-shrink-0">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting || isAdding}
                    size="sm"
                  >
                    {isAdding ? (
                      <>
                        Adding
                        <Spinner
                          animation="border"
                          size="sm"
                          className="ms-2"
                        />
                      </>
                    ) : (
                      ADD_NOTE
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          </div>
          <div className="previous-notes-container" ref={notesContainerRef}>
            <div className="previous-notes-title d-flex justify-content-between align-items-center">
              <div>Previous Notes</div>
              <div className="d-flex align-items-center">
                <label className="custom-toggle-switch">
                  <input
                    type="checkbox"
                    aria-label="Resolved Notes"
                    checked={showResolved}
                    onChange={() => setShowResolved(!showResolved)}
                  />
                  <span className="toggle-slider">
                    <span className="toggle-text off">
                      {NOTES_STATUS.PENDING}
                    </span>
                    <span className="toggle-text on">
                      {NOTES_STATUS.RESOLVED}
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {isLoading ? (
              <div className="my-3 mx-2">
                <CardSkeleton numberOfCards={2} />
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center d-flex flex-column align-items-center my-5">
                <NotesSvg />
                <small className="text-muted my-2">
                  {showResolved
                    ? NO_RESOLVED_NOTES_FOUND
                    : NO_PENDING_NOTES_FOUND}
                </small>
              </div>
            ) : (
              notes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  resolvingNoteId={resolvingNoteId}
                  handleResolveNote={handleResolveNote}
                  handleDeleteNote={handleDeleteNote}
                />
              ))
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Notes;
